import { ref } from "vue";

/**
 * @description Composable to handle IME (Input Method Editor) composition events
 * Prevents double-firing of input events when using Japanese, Chinese, Korean, etc.
 * @returns {Object} Object containing isComposing state and event handlers
 */
export const useIMEComposition = () => {
  const resetKey = ref(0);
  const isComposing = ref(false);

  /**
   * Handler for composition start event
   */
  const handleCompositionStart = () => {
    isComposing.value = true;
  };

  /**
   * Handler for composition end event
   * @param {Function} callback - Optional callback to execute after composition ends
   */
  const handleCompositionEnd = (callback?: (event: CompositionEvent, ...args: any[]) => void) => {
    return (event: CompositionEvent, ...args: any[]) => {
      isComposing.value = false;

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          callback?.(event, ...args);
        });
      });
    };
  };

  /**
   * Wrapper for input handlers that should be skipped during composition
   * @param {Function} inputHandler - The original input handler function
   * @returns {Function} Wrapped handler that respects composition state
   */
  const withIMECheck = (inputHandler: (event: Event, ...args: any[]) => void) => {
    return (event: Event, ...args: any[]) => {
      if ((event as any)?.isComposing || isComposing.value) {
        return;
      }
      inputHandler(event, ...args);
    };
  };

  return {
    isComposing,
    handleCompositionStart,
    handleCompositionEnd,
    withIMECheck,
    resetKey,
  };
};
